@import url("../../semantic/color.css");
@import url("../../semantic/typography.css");
@import url("../../semantic/spacing.css");
@import url("../../semantic/sizing.css");
@import url("../../semantic/layering.css");
@import url("../../semantic/motion.css");

/*
 * Lifted floating bubble anchored to its trigger via CSS Anchor Positioning (Baseline 2026).
 * Auto-flips on overflow; auto-hides when the anchor leaves the viewport.
 */

.doss-tooltip {
  position: fixed;
  z-index: var(--popover-z);

  /* Anchor binding: position-anchor is set per-instance via inline style with the same
   * dashed-ident the child carries as anchor-name. */
  position-area: top;

  /* Try the opposite side first, then both axes — covers all four flip permutations. */
  position-try-fallbacks: flip-block, flip-inline, flip-block flip-inline;

  /* Hide automatically when the anchor scrolls completely out of view. Overflow is handled
   * by position-try-fallbacks above, so a single keyword is enough here. */
  position-visibility: anchors-visible;
  margin: var(--popover-offset);
  max-inline-size: var(--lifted-max-inline-size);
  padding: var(--pill-padding-block) var(--pill-padding-inline);

  /* Inverted neutral pair — dark surface in light mode, light surface in dark mode.
   * High-contrast against the page surfaces (--page-background / --card-background) so the
   * tooltip reads as a callout rather than another panel. */
  background-color: var(--inverted-background);
  color: var(--inverted-text);
  border-radius: var(--action-radius);
  box-shadow: var(--shadow-lifted);
  font-size: var(--annotation-text-size);
  font-weight: var(--annotation-font-weight);
  line-height: var(--annotation-line-height);

  /* Default state — invisible, non-interactive. Layer count (opacity + transform) stays
   * constant across states so transitions interpolate cleanly. */
  pointer-events: none;
  opacity: 0;
  transform: scale(var(--overlay-entrance-start-scale));
  transform-origin: center;
  transition:
    opacity var(--floating-exit-duration) ease-in,
    transform var(--floating-exit-duration) ease-in;
}

/* Mapping from Floating UI's `Placement` to CSS Anchor Positioning's `position-area`.
 * Side variants (`top`, `right`, `bottom`, `left`) center on the cross-axis.
 * `-start` / `-end` align with the corresponding edge of the anchor via `span-*`:
 *   - For top/bottom (block-side) placements, the cross-axis is inline:
 *       `-start` → span-right (element's inline-start aligns with anchor's inline-start)
 *       `-end`   → span-left  (element's inline-end   aligns with anchor's inline-end)
 *   - For left/right (inline-side) placements, the cross-axis is block:
 *       `-start` → span-bottom (element's block-start aligns with anchor's block-start)
 *       `-end`   → span-top    (element's block-end   aligns with anchor's block-end) */
.doss-tooltip[data-placement="top-start"] { position-area: top span-right; }
.doss-tooltip[data-placement="top-end"] { position-area: top span-left; }
.doss-tooltip[data-placement="bottom"] { position-area: bottom; }
.doss-tooltip[data-placement="bottom-start"] { position-area: bottom span-right; }
.doss-tooltip[data-placement="bottom-end"] { position-area: bottom span-left; }
.doss-tooltip[data-placement="right"] { position-area: right; }
.doss-tooltip[data-placement="right-start"] { position-area: right span-bottom; }
.doss-tooltip[data-placement="right-end"] { position-area: right span-top; }
.doss-tooltip[data-placement="left"] { position-area: left; }
.doss-tooltip[data-placement="left-start"] { position-area: left span-bottom; }
.doss-tooltip[data-placement="left-end"] { position-area: left span-top; }

/* Reveal on hover (any descendant of the host) or keyboard focus.
 * Using :has() lets the rule work even though the host is display: contents — the host
 * has no box itself, so :hover / :focus-within on it would be unreliable. */
.doss-tooltip-host:has(:hover) > .doss-tooltip,
.doss-tooltip-host:has(:focus-visible) > .doss-tooltip {
  opacity: 1;
  transform: scale(1);
  transition:
    opacity var(--floating-enter-duration) ease-out var(--floating-enter-delay),
    transform var(--floating-enter-duration) ease-out var(--floating-enter-delay);
}

/* A popup trigger's tooltip hides while its popup is open, since the popup shows what the trigger
 * does. Inline expand toggles have no `aria-haspopup`, so their tooltips still show. */
.doss-tooltip-host:has(> [aria-haspopup][aria-expanded="true"]) > .doss-tooltip {
  opacity: 0;
  transform: scale(var(--overlay-entrance-start-scale));
}

/* Opt-in truncation suppression — when the consumer sets onlyShowIfReferenceTruncated
 * and the reference is NOT clipped, hide the tooltip outright. visibility: hidden wins
 * over the opacity rule above so we don't have to override two transitions. */
.doss-tooltip[data-truncated="false"] {
  visibility: hidden;
}

/* Opacity stays, transforms are skipped under reduced motion. */
@media (prefers-reduced-motion: reduce) {
  .doss-tooltip,
  .doss-tooltip-host:has(:hover) > .doss-tooltip,
  .doss-tooltip-host:has(:focus-visible) > .doss-tooltip {
    transform: none;
    transition-property: opacity;
  }
}
